import { AppRoot } from "@/app-shell";
import { environment } from "@/config";
import "@/posthog";
import { registerPwa } from "@/services/pwa";
import { queryClient } from "@/services/query-client";
import { recordError } from "@/services/record-error";
import { readServerContentState } from "@/services/server-content-state";

import "@fontsource/fira-sans/100.css";
import "@fontsource/fira-sans/200.css";
import "@fontsource/fira-sans/300.css";
import "@fontsource/fira-sans/400.css";
import "@fontsource/fira-sans/500.css";
import "@fontsource/fira-sans/600.css";
import "@fontsource/fira-sans/700.css";
import "@fontsource/fira-sans/800.css";
import "@fontsource/fira-sans/900.css";
import "@fontsource/karla/200.css";
import "@fontsource/karla/300.css";
import "@fontsource/karla/400-italic.css";
import "@fontsource/karla/400.css";
import "@fontsource/karla/500-italic.css";
import "@fontsource/karla/500.css";
import "@fontsource/karla/600.css";
import "@fontsource/karla/700.css";
import "@fontsource/karla/800.css";
import * as Sentry from "@sentry/react";
import { hydrate } from "@tanstack/react-query";
import { createRoot, hydrateRoot } from "react-dom/client";
import "react-phone-number-input/style.css";

import App from "./App";
import "./index.css";

Sentry.init({
  dsn: "https://f79b611e68e398beefcd2cf85ac43ccb@sentry.selego.co/218",
  enabled: environment === "production",
  sendDefaultPii: true,
  integrations: [
    Sentry.replayIntegration({
      maskAllText: false,
      maskAllInputs: false,
      blockAllMedia: false,
      // Sentry masks these attributes by default, which hides the labels of
      // icon-only buttons (aria-label) and field hints in replays.
      maskAttributes: [],
      unmask: [
        'input:not([type="password"]):not([name*="password"]):not([id*="password"])',
        "textarea",
        "select",
        "option",
      ],
    }),
    Sentry.browserTracingIntegration(),
  ],
  replaysSessionSampleRate: 1.0,
  replaysOnErrorSampleRate: 1.0,
  tracesSampleRate: 1.0,
  tracePropagationTargets: [
    "localhost",
    "localhost:8080",
    "api.volvamed.fr",
    /^https:\/\/api\.volvamed\.fr/,
  ],
  beforeSend(event, hint) {
    if (
      event.tags?.expected === true ||
      event.tags?.expected === "true" ||
      hint?.originalException?.expected === true
    ) {
      return null;
    }

    const exception = event.exception?.values?.[0];
    const frames = exception?.stacktrace?.frames || [];

    if (
      String(exception?.value || "").includes("Java object is gone") ||
      String(exception?.value || "") ===
        "undefined is not an object (evaluating 'window.webkit.messageHandlers')" ||
      frames.some((frame) =>
        String(frame.filename || "").startsWith(
          "iabjs://navigation_performance_logger_android"
        )
      )
    ) {
      return null;
    }

    return event;
  },
});

registerPwa();

const serverContentState = readServerContentState();
const rootElement = document.getElementById("root");
const tree = (
  <AppRoot queryClient={queryClient}>
    <App />
  </AppRoot>
);

// A public content page arrives already rendered by the server: its route
// modules are loaded first so hydration keeps that HTML instead of showing
// the Suspense loader, then React attaches to the existing markup.
async function hydrateServerContent() {
  try {
    const { preloadPublicContentRoute } = await import("@/routes");
    await preloadPublicContentRoute(window.location.pathname);
  } catch (error) {
    // A stale chunk after a deployment: render from scratch instead of
    // leaving the server markup without any JavaScript.
    recordError(error, {
      source: "main",
      operation: "hydrateServerContent",
    });
    createRoot(rootElement).render(tree);
    return;
  }
  hydrateRoot(rootElement, tree, {
    onRecoverableError(error, errorInfo) {
      recordError(error, {
        source: "main",
        operation: "hydrateRoot",
        attributes: { component_stack: errorInfo?.componentStack || "" },
      });
    },
  });
}

// The server sends the query cache it rendered with: the pages read the same
// data on their first render, so hydration keeps the server HTML without
// fetching it again (the data is fresh for the default 30 s staleTime).
if (serverContentState) {
  hydrate(queryClient, serverContentState);
  hydrateServerContent();
} else {
  createRoot(rootElement).render(tree);
}
